import React, { useState } from 'react';
import CreatableSelect from 'react-select/creatable';

/**
 * CreatableMultiSelect — A multi-select that also allows adding new values by typing.
 *
 * User types a value and presses Enter, Tab, or comma to create a tag.
 * Optionally accepts predefined options to choose from.
 *
 * Props:
 *   value       - Array of current values (strings or numbers), e.g. ["Active", "Pending"]
 *   onChange    - Callback with the updated array of values
 *   options     - (optional) Predefined options: [{ label, value }] or string[]
 *   placeholder - Input placeholder text
 *   disabled    - Whether the input is disabled/read-only
 *   autoNumeric - Auto-convert values to numbers if all values are numeric (default: true)
 *   size        - 'sm' | 'md' (default: 'sm')
 *   menuPortalTarget - Portal target for dropdown menu (default: document.body)
 *   splitOnWhitespace - Also treat spaces as tag separators when typing/pasting
 *                       (default: false). Use for values that can't contain spaces.
 *   validateValue - (optional) (value: string) => boolean. When provided, entries
 *                   that return false are rejected (not added) and left in the input.
 *   onInvalid   - (optional) (rejected: string[]) => void. Called with the values
 *                 that failed validateValue, for surfacing an error message.
 *
 * Usage:
 *   <CreatableMultiSelect
 *       value={['Active Client', 'Active']}
 *       onChange={(values) => setFilter(values)}
 *       placeholder='Type and press Enter...'
 *   />
 */

const components = {
    DropdownIndicator: null,
};

const createOption = (label) => ({
    label: label.trim(),
    value: label.trim(),
});

const CreatableMultiSelect = ({
    value = [],
    onChange,
    options: predefinedOptions,
    placeholder = 'Type a value and press Enter...',
    disabled = false,
    autoNumeric = true,
    size = 'sm',
    menuPortalTarget,
    className,
    splitOnWhitespace = false,
    validateValue,
    onInvalid,
}) => {
    const [inputValue, setInputValue] = useState('');

    // Optional per-value validation. When validateValue is provided, entries
    // that fail it are dropped instead of added, and onInvalid (if provided) is
    // called once with the list of rejected values so the caller can surface a
    // message. Stripping wrapping quotes so validation sees the bare value.
    const stripQuotes = (v) => String(v).replace(/^["']|["']$/g, '');
    const partitionValid = (candidates) => {
        if (!validateValue) return { valid: candidates, invalid: [] };
        const valid = [];
        const invalid = [];
        candidates.forEach(v => (validateValue(stripQuotes(v)) ? valid : invalid).push(v));
        return { valid, invalid };
    };

    // Delimiters used to split typed/pasted text into separate tags.
    // Commas, newlines and tabs are always separators. Spaces are only
    // treated as separators when splitOnWhitespace is set (e.g. for values
    // that can never legitimately contain spaces, like domains).
    const splitDelimiters = splitOnWhitespace ? /[\s,]+/ : /[,\n\t]+/;

    // Normalize predefined options
    const selectOptions = predefinedOptions
        ? predefinedOptions.map(o => typeof o === 'string' ? { label: o, value: o } : o)
        : undefined;

    // Normalize value array into react-select format — use option label if available
    const selectValue = (value || []).map(v => {
        const matchedOption = selectOptions?.find(o => String(o.value) === String(v));
        return {
            label: matchedOption ? matchedOption.label : String(v),
            value: v,
        };
    });

    /**
     * Per-value type coercion:
     * - Wrapped in quotes ("500") → force string "500"
     * - Numeric without quotes (199) → number 199
     * - Non-numeric (Active) → string "Active"
     */
    const coerceValue = (v) => {
        if (typeof v !== 'string') return v;
        // Explicitly quoted — strip quotes, keep as string
        if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
            return v.slice(1, -1);
        }
        // Numeric — convert to number
        if (v !== '' && !isNaN(v)) return Number(v);
        // Default — string
        return v;
    };

    const coerceArray = (arr) => autoNumeric ? arr.map(coerceValue) : arr;

    const handleChange = (newValue) => {
        const raw = (newValue || []).map(v => v.value);
        onChange?.(coerceArray(raw));
    };

    const handleInputChange = (newInput) => {
        setInputValue(newInput);
    };

    const handleKeyDown = (event) => {
        if (!inputValue) return;

        if (['Enter', 'Tab', ','].includes(event.key)) {
            event.preventDefault();
            // Split typed input into tags using the configured delimiters.
            const newValues = inputValue.split(splitDelimiters).map(v => v.trim()).filter(Boolean);
            const current = (value || []).map(String);
            const candidates = newValues.filter(v => !current.includes(stripQuotes(v)));
            const { valid, invalid } = partitionValid(candidates);

            if (valid.length > 0) {
                const updated = [...(value || []), ...valid.map(coerceValue)];
                onChange?.(updated);
            }
            if (invalid.length > 0) onInvalid?.(invalid);
            // Keep rejected text in the input so the user can fix it.
            setInputValue(invalid.length > 0 ? invalid.join(' ') : '');
        }
    };

    const handlePaste = (event) => {
        event.preventDefault();
        const pasted = event.clipboardData.getData('text');
        const newValues = pasted.split(splitDelimiters).map(v => v.trim()).filter(Boolean);
        const current = (value || []).map(String);
        const candidates = newValues.filter(v => !current.includes(stripQuotes(v)));
        const { valid, invalid } = partitionValid(candidates);

        if (valid.length > 0) {
            const updated = [...(value || []), ...valid.map(coerceValue)];
            onChange?.(updated);
        }
        if (invalid.length > 0) onInvalid?.(invalid);
        // Keep rejected text in the input so the user can fix it.
        setInputValue(invalid.length > 0 ? invalid.join(' ') : '');
    };

    const customStyles = {
        control: (base) => ({
            ...base,
            fontSize: size === 'sm' ? 12 : 14,
        }),
        valueContainer: (base) => ({
            ...base,
            padding: size === 'sm' ? '0 6px' : '2px 8px',
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
        }),
        multiValue: (base) => ({
            ...base,
            borderRadius: 3,
        }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        placeholder: (base) => ({
            ...base,
            color: '#aaa',
        }),
    };

    return (
        <CreatableSelect
            className={`react-select-container${className ? ' ' + className : ''}`}
            classNamePrefix='react-select'
            components={selectOptions ? undefined : components}
            inputValue={inputValue}
            isClearable
            isMulti
            isDisabled={disabled}
            menuIsOpen={selectOptions ? undefined : false}
            closeMenuOnSelect={false}
            onChange={handleChange}
            onInputChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            options={selectOptions}
            placeholder={placeholder}
            value={selectValue}
            styles={customStyles}
            menuPortalTarget={menuPortalTarget || document.body}
            menuPosition='fixed'
        />
    );
};

export default CreatableMultiSelect;
